// Phase 40 Plan 02 Task 2 — SignInModal.
// CONTEXT D-09. UI-SPEC §1. 40-PATTERNS lines 240-258.
//
// Three provider buttons (Google / Apple / Facebook) plus a magic-link
// email form. Modal chrome cloned from SettingsPanel; magic-link primary
// CTA styled per AskTendUpgrade. UK English; Lucide icons only; brand
// SVGs from public/brand/ (operator-gated Task 2.0). Inputs honour the
// 16px iOS-zoom floor (UAT2-GAP-07).

import React, { useState, useRef, useEffect } from 'react';
import { Loader2, X } from 'lucide-react';
import {
  signInWithProvider,
  signInWithMagicLink,
  signInWithEmailOtp,
  verifyEmailOtp,
} from './supabaseClient.js';

const RESEND_COOLDOWN_SECONDS = 60;

export default function SignInModal({ onClose, onSuccessNavigate }) {
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [magicSent, setMagicSent] = useState(false);
  const [email, setEmail] = useState('');
  const [magicError, setMagicError] = useState('');

  // 'magic' (default) renders the magic-link email form. 'otp' switches to
  // the 6-digit-code flow — same email input, then a code-entry stage.
  const [authMode, setAuthMode] = useState('magic');
  const [otpStage, setOtpStage] = useState('email'); // 'email' | 'code'
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendIn, setResendIn] = useState(0);
  const titleRef = useRef(null);
  const otpInputs = useRef([]);

  useEffect(() => {
    titleRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  // Provider buttons invoke signInWithProvider('google') / signInWithProvider('apple')
  // / signInWithProvider('facebook') — see provider() factory below.
  const handleProvider = async (provider) => {
    if (loadingProvider) return;
    setLoadingProvider(provider);
    try {
      await signInWithProvider(provider);
      // Successful flow → full-page redirect happens; if it doesn't, restore.
    } catch {
      setLoadingProvider(null);
    }
  };

  const handleMagic = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setMagicError('');
    const { error } = await signInWithMagicLink(email.trim());
    if (error) {
      setMagicError(error.message || 'Could not send link.');
      return;
    }
    setMagicSent(true);
    // NOTE: onSuccessNavigate intentionally NOT called here. Magic-link is
    // async — user must see the "Check your inbox shortly" state before they
    // navigate away. The modal's × button or backdrop click closes it once
    // they've read the message. Provider OAuth paths handle their own
    // navigation via window.location redirect, so this only affects email.
  };

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || otpSending || resendIn > 0) return;
    setOtpError('');
    setOtpSending(true);
    const { error } = await signInWithEmailOtp(email.trim());
    setOtpSending(false);
    if (error) {
      setOtpError(error.message || 'Could not send code.');
      return;
    }
    setOtpStage('code');
    setOtpDigits(['', '', '', '', '', '']);
    setResendIn(RESEND_COOLDOWN_SECONDS);
    setTimeout(() => otpInputs.current[0]?.focus(), 0);
  };

  const handleOtpDigitChange = (idx, value) => {
    const v = value.replace(/\D/g, '');
    if (!v) {
      const next = [...otpDigits];
      next[idx] = '';
      setOtpDigits(next);
      return;
    }
    // Paste-of-6 support: spread digits across the row from `idx`.
    if (v.length > 1) {
      const next = [...otpDigits];
      const chars = v.slice(0, 6 - idx).split('');
      chars.forEach((c, i) => { next[idx + i] = c; });
      setOtpDigits(next);
      const focusIdx = Math.min(idx + chars.length, 5);
      setTimeout(() => otpInputs.current[focusIdx]?.focus(), 0);
      return;
    }
    const next = [...otpDigits];
    next[idx] = v;
    setOtpDigits(next);
    if (idx < 5) setTimeout(() => otpInputs.current[idx + 1]?.focus(), 0);
  };

  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0) {
      setTimeout(() => otpInputs.current[idx - 1]?.focus(), 0);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    const token = otpDigits.join('');
    if (token.length !== 6 || otpVerifying) return;
    setOtpError('');
    setOtpVerifying(true);
    const { error } = await verifyEmailOtp(email.trim(), token);
    setOtpVerifying(false);
    if (error) {
      const msg = error.message || '';
      if (/expired/i.test(msg)) setOtpError('That code has expired. Request a new one.');
      else if (/invalid|incorrect/i.test(msg)) setOtpError('That code is not correct. Check and try again.');
      else setOtpError(msg || 'Could not verify code.');
      return;
    }
    // AuthProvider's onAuthStateChange picks up the new session — onClose
    // tears the modal down so the signed-in UI takes over.
    onSuccessNavigate?.();
    onClose?.();
  };

  const buttonBase = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    minHeight: 44,
    background: 'var(--c-bg)',
    color: 'var(--c-text)',
    border: '1px solid var(--c-border)',
    borderRadius: 24,
    padding: '10px 16px',
    fontSize: '0.9375rem',
    fontWeight: 500,
    fontFamily: 'Lora, serif',
    cursor: 'pointer',
  };

  const provider = (id, label, brandSrc) => {
    const isLoading = loadingProvider === id;
    const dim = loadingProvider && loadingProvider !== id;
    return (
      <button
        type="button"
        key={id}
        onClick={() => handleProvider(id)}
        aria-label={label}
        disabled={!!loadingProvider}
        style={{
          ...buttonBase,
          opacity: dim ? 0.5 : 1,
          cursor: loadingProvider ? 'default' : 'pointer',
        }}
      >
        {isLoading ? (
          <>
            <Loader2 size={18} aria-hidden="true" className="t-spin" />
            <span>Redirecting…</span>
          </>
        ) : (
          <>
            <img src={brandSrc} alt="" aria-hidden="true" width={22} height={22} style={{ display: 'block', flexShrink: 0 }} />
            <span>{label}</span>
          </>
        )}
      </button>
    );
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'var(--c-overlay)', zIndex: 2000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-modal-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--c-bg)', borderRadius: 16, padding: 28,
          maxWidth: 440, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          position: 'relative', marginTop: 'auto', marginBottom: 'auto',
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close sign-in"
          style={{
            position: 'absolute', top: 12, right: 12, width: 44, height: 44,
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text-2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <X size={20} aria-hidden="true" />
        </button>

        <h1
          id="signin-modal-title"
          ref={titleRef}
          tabIndex={-1}
          style={{
            fontFamily: "'Playfair Display', serif", fontSize: '1.625rem', fontWeight: 700,
            color: 'var(--c-heading)', margin: '0 0 12px 0', outline: 'none',
          }}
        >
          Save your garden to the cloud
        </h1>
        <p style={{ fontSize: '0.9375rem', color: 'var(--c-text-2)', margin: '0 0 24px 0', lineHeight: 1.55 }}>
          Sign in to back up your plants and access them from any device.
        </p>

        {!magicSent && !(authMode === 'otp' && otpStage === 'code') ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {provider('google',   'Continue with Google',   `${import.meta.env.BASE_URL}brand/google.svg`)}
              {provider('apple',    'Continue with Apple',    `${import.meta.env.BASE_URL}brand/apple.svg`)}
              {provider('facebook', 'Continue with Facebook', `${import.meta.env.BASE_URL}brand/facebook.svg`)}
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0',
              color: 'var(--c-text-3)',
            }}>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--c-border)', margin: 0 }} />
              <small className="t-tiny" style={{ fontSize: '0.75rem' }}>or</small>
              <hr style={{ flex: 1, border: 0, borderTop: '1px solid var(--c-border)', margin: 0 }} />
            </div>

            <form onSubmit={authMode === 'otp' ? sendOtp : handleMagic} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label htmlFor="signin-email" className="label-block" style={{ fontSize: '0.8125rem', color: 'var(--c-text-2)' }}>
                Email address
              </label>
              <input
                id="signin-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={{
                  padding: '10px 12px', borderRadius: 8,
                  border: '1px solid var(--c-border)', fontSize: 'var(--font-input)',
                  background: 'var(--c-bg)', color: 'var(--c-text)', width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              {authMode === 'magic' && magicError && (
                <small style={{ color: 'var(--c-danger)', fontSize: '0.8125rem' }}>{magicError}</small>
              )}
              {authMode === 'otp' && otpError && (
                <small style={{ color: 'var(--c-danger)', fontSize: 13 }}>{otpError}</small>
              )}
              <button
                type="submit"
                disabled={authMode === 'otp' && otpSending}
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  background: 'var(--c-brand-deep)', color: '#fff',
                  border: '2px solid var(--c-brand-deep)',
                  borderRadius: 24, padding: '14px 28px', fontSize: 16, fontWeight: 700,
                  cursor: (authMode === 'otp' && otpSending) ? 'default' : 'pointer',
                  width: '100%', minHeight: 48, fontFamily: 'inherit',
                  marginTop: 4, boxShadow: '0 2px 8px rgba(45,90,45,0.25)',
                  opacity: (authMode === 'otp' && otpSending) ? 0.7 : 1,
                }}
              >
                {authMode === 'magic' && <span>Email me a sign-in link</span>}
                {authMode === 'otp' && otpSending && (
                  <>
                    <Loader2 size={18} aria-hidden="true" className="t-spin" />
                    <span>Sending code…</span>
                  </>
                )}
                {authMode === 'otp' && !otpSending && <span>Send code</span>}
              </button>

              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => {
                    setAuthMode((m) => (m === 'magic' ? 'otp' : 'magic'));
                    setMagicError('');
                    setOtpError('');
                  }}
                  style={{
                    background: 'none', border: 'none', padding: 4,
                    color: 'var(--c-brand-deep)', fontSize: 13, fontWeight: 600,
                    cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
                  }}
                >
                  {authMode === 'magic' ? 'Send a code instead' : 'Send a sign-in link instead'}
                </button>
              </div>
              {authMode === 'otp' && (
                <small style={{ fontSize: 12, color: 'var(--c-text-3)', textAlign: 'center', lineHeight: 1.4 }}>
                  Tip: code-based sign-in works reliably on mobile when the link doesn't.
                </small>
              )}
            </form>
          </>
        ) : authMode === 'otp' && otpStage === 'code' ? (
          <form onSubmit={verifyOtp} aria-label="Verify code">
            <h3 style={{
              fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 600,
              color: 'var(--c-heading)', margin: '0 0 8px 0',
            }}>
              Enter your 6-digit code
            </h3>
            <p style={{ fontSize: 14, color: 'var(--c-text-2)', margin: '0 0 16px 0', lineHeight: 1.55 }}>
              We sent a code to <strong>{email}</strong>. It expires in 10 minutes.
            </p>

            <div
              role="group"
              aria-label="6-digit code"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12 }}
            >
              {otpDigits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => { otpInputs.current[i] = el; }}
                  type="text"
                  inputMode="numeric"
                  autoComplete={i === 0 ? 'one-time-code' : 'off'}
                  maxLength={i === 0 ? 6 : 1}
                  value={d}
                  aria-label={`Digit ${i + 1}`}
                  onChange={(e) => handleOtpDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                  style={{
                    flex: 1, minWidth: 0, height: 56, textAlign: 'center',
                    fontSize: 22, fontWeight: 700, fontFamily: 'Lora, serif',
                    borderRadius: 8, border: '1px solid var(--c-border)',
                    background: 'var(--c-bg)', color: 'var(--c-text)',
                    boxSizing: 'border-box',
                  }}
                />
              ))}
            </div>

            {otpError && (
              <small style={{ color: 'var(--c-danger)', fontSize: 13, display: 'block', marginBottom: 8 }}>{otpError}</small>
            )}

            <button
              type="submit"
              disabled={otpDigits.join('').length !== 6 || otpVerifying}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                background: 'var(--c-brand-deep)', color: '#fff',
                border: '2px solid var(--c-brand-deep)',
                borderRadius: 24, padding: '14px 28px', fontSize: 16, fontWeight: 700,
                cursor: (otpVerifying || otpDigits.join('').length !== 6) ? 'default' : 'pointer',
                width: '100%', minHeight: 48, fontFamily: 'inherit',
                boxShadow: '0 2px 8px rgba(45,90,45,0.25)',
                opacity: (otpVerifying || otpDigits.join('').length !== 6) ? 0.7 : 1,
              }}
            >
              {otpVerifying ? (
                <>
                  <Loader2 size={18} aria-hidden="true" className="t-spin" />
                  <span>Verifying…</span>
                </>
              ) : <span>Verify</span>}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, gap: 12 }}>
              <button
                type="button"
                onClick={() => {
                  setOtpStage('email');
                  setOtpDigits(['', '', '', '', '', '']);
                  setOtpError('');
                }}
                style={{
                  background: 'none', border: 'none', padding: 4,
                  color: 'var(--c-text-2)', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit',
                }}
              >
                Use a different email
              </button>
              <button
                type="button"
                onClick={sendOtp}
                disabled={resendIn > 0 || otpSending}
                style={{
                  background: 'none', border: 'none', padding: 4,
                  color: resendIn > 0 ? 'var(--c-text-3)' : 'var(--c-brand-deep)',
                  fontSize: 13, fontWeight: 600,
                  cursor: resendIn > 0 ? 'default' : 'pointer',
                  textDecoration: resendIn > 0 ? 'none' : 'underline',
                  fontFamily: 'inherit',
                }}
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <h3 style={{
              fontFamily: "'Playfair Display', serif", fontSize: '1.125rem', fontWeight: 600,
              color: 'var(--c-heading)', margin: '0 0 10px 0',
            }}>
              Check your inbox shortly.
            </h3>
            <p style={{ fontSize: '0.9375rem', color: 'var(--c-text-2)', margin: '0 0 12px 0', lineHeight: 1.55 }}>
              We've sent a sign-in link to <strong>{email}</strong>. Tap it to verify your email and finish signing in.
            </p>
            <p style={{ fontSize: '0.8125rem', color: 'var(--c-text-3)', margin: '0 0 16px 0', lineHeight: 1.5 }}>
              The email may take a minute to arrive — check your spam folder if you don't see it.
            </p>
            <button
              type="button"
              onClick={() => { setMagicSent(false); setEmail(''); }}
              style={{
                background: 'var(--c-btn-bg)', color: 'var(--c-btn-text)', border: 'none',
                borderRadius: 24, padding: '10px 22px', fontSize: '0.875rem', fontWeight: 600,
                cursor: 'pointer', minHeight: 44,
              }}
            >
              Use a different email
            </button>
          </div>
        )}

        <small className="t-tiny" style={{
          display: 'block', textAlign: 'center', marginTop: 20,
          fontSize: '0.75rem', color: 'var(--c-text-3)',
        }}>
          By signing in you agree to the <a href="#" style={{ color: 'var(--c-brand-deep)' }}>Terms</a> and <a href="#" style={{ color: 'var(--c-brand-deep)' }}>Privacy Policy</a>.
        </small>

      </div>
    </div>
  );
}
